import React, { useState } from 'react';
import { List, Popover, Button, Typography, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { mantyIntegrations } from 'routes/IntegrationsList/fullIntegrationList';
import _ from 'lodash';
import Flex from 'ui/Flex/Flex';
import { useTranslation } from 'react-i18next';
import { IntegrationT } from 'routes/IntegrationsList/types';
import IntegrationCheckbox from './IntegrationCheckbox';

const IntegrationCheckboxesByDomain = ({
    domain,
    integrationsByDomain,
    newSoftwares,
    setNewSoftwares,
    isLast,
}: {
    domain: string;
    integrationsByDomain: Record<string, typeof mantyIntegrations>;
    newSoftwares: string[];
    setNewSoftwares: (newSoftwares: string[]) => void;
    isLast: boolean;
}) => {
    const { t } = useTranslation();
    return (
        <>
            <Typography variant="body1Bold" sx={{ p: 1, mt: 1 }}>
                {t(`integrations.${domain}`)}
            </Typography>
            <List>
                {integrationsByDomain[domain]
                    .sort((a, b) => (a.title > b.title ? 1 : -1))
                    .map((software) => (
                        <IntegrationCheckbox
                            key={software.software}
                            software={software}
                            newSoftwares={newSoftwares}
                            setNewSoftwares={setNewSoftwares}
                        />
                    ))}
            </List>
            {!isLast && <Divider />}
        </>
    );
};

const AddIntegrationButton = ({
    currentIntegrations,
    newSoftwares,
    setNewSoftwares,
}: {
    currentIntegrations: IntegrationT[];
    newSoftwares: string[];
    setNewSoftwares: (newSoftwares: string[]) => void;
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const integrationsByDomain = _.groupBy(
        mantyIntegrations.filter(
            (i) =>
                !currentIntegrations
                    .map((integration) => integration.software)
                    .includes(i.software.toUpperCase()),
        ),
        (i) => i.type,
    );
    const domains = Object.keys(integrationsByDomain).sort();
    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                sx={{ height: '1rem', width: '300px' }}
                onClick={handleClick}
                endIcon={<ExpandMoreIcon fontSize="small" />}
            >
                {t('support.clientConfiguration.integrations.addIntegration')}
            </Button>
            <Popover
                sx={{ maxHeight: '500px' }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                onClose={handleClose}
            >
                <Flex direction="column">
                    <Flex
                        direction="column"
                        sx={{
                            p: 1,
                            maxHeight: '400px',
                            overflowY: 'auto',
                        }}
                    >
                        {domains.map((domain, index) => {
                            const isLast: boolean =
                                index === domains.length - 1;
                            return (
                                <IntegrationCheckboxesByDomain
                                    key={domain}
                                    domain={domain.toString()}
                                    integrationsByDomain={integrationsByDomain}
                                    newSoftwares={newSoftwares}
                                    setNewSoftwares={setNewSoftwares}
                                    isLast={isLast}
                                />
                            );
                        })}
                    </Flex>
                    <Button
                        sx={{ m: 1, width: '50%', alignSelf: 'center' }}
                        variant="contained"
                        color="primary"
                        onClick={handleClose}
                    >
                        {t('common.validate')}
                    </Button>
                </Flex>
            </Popover>
        </>
    );
};

export default React.memo(AddIntegrationButton);
